import { Anchor } from 'src/design/atoms/Anchor';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import React from 'react';
import {
  JobChangeOrderDto,
  JobInvoiceDataDto,
} from 'src/api/FiretrolApiClient';
import { Currency } from 'src/design/atoms/Currency';
import { ChangeOrderDialog } from 'src/routes/CTC/ViewCTCJob/components/ChangeOrderDialog';
import { InvoicesDialog } from 'src/routes/CTC/ViewCTCJob/components/InvoicesDialog';

export interface JobData {
  percentComplete: number;
  contractAmount: number;
  overUnder: number;
  contractEarned: number;
  original: number;
  costToDate: number;
  postedCOTotal: number;
  costAtCompletion: number;
  contractToDate: number;
  adjustedRevenue: number;
  expectedContract: number;
  grossMarginDollar: number;
  grossMarginPercent: number;
  grossBilled: number;
  retentionHeld: number;
  retentionBilled: number;
  netBilled: number;
  jtdCosts: number;
}
interface JobDetailsProps {
  title: string;
  jobData: JobData;
  costAtCompletion: number;
  adjusted?: number;
  ctc?: number;
  isCalc?: boolean;
  invoiceData?: JobInvoiceDataDto[];
  changeOrderData?: JobChangeOrderDto[];
}
export const JobDetails: React.FC<JobDetailsProps> = ({
  jobData,
  title,
  adjusted = 0,
  costAtCompletion,
  isCalc = false,
  ctc = 0,
  invoiceData,
  changeOrderData,
}) => {
  const [invoicesOpen, setInvoicesOpen] = React.useState(false);
  const [changeOrdersOpen, setChangeOrdersOpen] = React.useState(false);
  const revenueValue = adjusted
    ? jobData.adjustedRevenue + adjusted
    : jobData.adjustedRevenue;
  //const updated = (revenueValue / jobData.contractAmount) * 100;

  const expectedContract = jobData.contractToDate + adjusted;
  const grossMargin = expectedContract - costAtCompletion;
  const percentage = (grossMargin / expectedContract) * 100;
  // You get gross margin by subtracting Expected contract and Cost at completion. Gross margin % should then be Gross margin divided by contract to date.

  const renderRow = (
    label: string,
    value: React.ReactNode,
    bold: boolean = false,
  ) => {
    return (
      <Group justify="space-between">
        <Text span c="black" fw={bold ? 'bold' : 'normal'}>
          {label}:
        </Text>
        {value}
      </Group>
    );
  };

  const renderTextRow = (label: string, value: string, bold: boolean = false) =>
    renderRow(
      label,
      <Text span c="black" fw={bold ? 'bold' : 'normal'}>
        {value}
      </Text>,
      bold,
    );
  const renderCurrencyRow = (
    label: string,
    value: number,
    bold: boolean = false,
  ) =>
    renderRow(
      label,
      <Text span c="black" fw={bold ? 'bold' : 'normal'}>
        <Currency precise value={value} placeholder="--" />
      </Text>,
      bold,
    );
  const renderPercentageRow = (
    label: string,
    value: number,
    bold: boolean = false,
  ) =>
    renderTextRow(
      label,
      Number.isFinite(value) ? `${Math.round(value)}%` : '--',
      bold,
    );

  return (
    <Paper>
      <Title order={5} pb="xs">
        {title.toUpperCase()}
      </Title>
      <Stack gap="lg">
        <Stack gap={3}>
          {renderCurrencyRow('Original Contract', jobData.original)}
          {renderRow(
            "Posted CO's",
            changeOrderData ? (
              <>
                <Anchor onClick={() => setChangeOrdersOpen(true)} c="blue">
                  {
                    <Currency
                      precise
                      value={jobData.postedCOTotal}
                      placeholder="--"
                    />
                  }
                </Anchor>
                <ChangeOrderDialog
                  open={changeOrdersOpen}
                  changeOrders={changeOrderData}
                  handleClose={() => setChangeOrdersOpen(false)}
                />
              </>
            ) : (
              <Text span c="black">
                {
                  <Currency
                    precise
                    value={jobData.postedCOTotal}
                    placeholder="--"
                  />
                }
              </Text>
            ),
          )}
          <Divider my={3} />
          {renderCurrencyRow('Contract to Date', jobData.contractToDate)}
          {renderRow(
            'Revenue Adjustment',
            <Text span c={isCalc ? 'yellow' : 'black'}>
              {<Currency precise value={revenueValue} placeholder="--" />}
            </Text>,
          )}
          <Divider my={3} />
          {renderCurrencyRow('Expected Contract', expectedContract, true)}
        </Stack>
        <Stack gap={3}>
          {renderCurrencyRow('Cost at Completion', costAtCompletion)}
          <Divider my={3} />
          {renderCurrencyRow('Gross Margin', grossMargin, false)}
          {renderPercentageRow(
            'Gross Margin %',
            Number.isFinite(percentage) ? percentage : 0,
            true,
          )}
        </Stack>
        <Stack gap={3}>
          <Divider my={3} />
          {renderRow(
            'Gross Billed',
            invoiceData ? (
              <>
                <Anchor onClick={() => setInvoicesOpen(true)} c="blue">
                  {
                    <Currency
                      precise
                      value={jobData.grossBilled}
                      placeholder="--"
                    />
                  }
                </Anchor>
                <InvoicesDialog
                  open={invoicesOpen}
                  invoices={invoiceData}
                  handleClose={() => setInvoicesOpen(false)}
                />
              </>
            ) : (
              <Text span c="black">
                {
                  <Currency
                    precise
                    value={jobData.grossBilled}
                    placeholder="--"
                  />
                }
              </Text>
            ),
          )}
          {renderCurrencyRow('Retention Held', jobData.retentionHeld)}
          {renderCurrencyRow('Retention Billed', jobData.retentionBilled)}
          <Divider my={3} />
          {renderCurrencyRow('Net Billed', jobData.netBilled, true)}
        </Stack>
        <Stack gap={3}>
          {renderCurrencyRow('Costs to Date', jobData.costToDate)}
          {renderCurrencyRow('Contract Earned', jobData.contractEarned)}
          <Divider my={3} />
          {renderCurrencyRow('Over/Under Billed', jobData.overUnder)}
          {renderPercentageRow('Percent Complete', jobData.percentComplete)}
        </Stack>
      </Stack>
    </Paper>
  );
};
